import { type KeyboardEvent } from 'react'
import { type DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import { useLocation, useNavigate } from 'react-router-dom'
import { Badge, ChevronRightIcon, KitMedicalIcon } from '@quickmd-llc/design-system'
import { FontAwesomeIcon } from '@quickmd-llc/react-fontawesome'
import { faGripDotsVertical } from '@quickmd-llc/fontawesome-pro'
import { type ServiceTypeListItemAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { setService } from '@/store/viewService/ViewServiceActions'

type ServiceTypeRowProps = {
  type?: 'empty'
  t?: ServiceTypeListItemAdminApiOutContract
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  isDragging?: boolean
  isLast?: boolean
}

export const ServiceTypeRow = ({
  type,
  t,
  dragHandleProps,
  isDragging = false,
  isLast = false,
}: ServiceTypeRowProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  if (type === 'empty' || !t) {
    return (
      <div className='px-[24px] py-[40px] text-center text-sm font-medium text-text-light'>
        Currently no service types
      </div>
    )
  }

  const serviceTypeId = t.ServiceTypeId ?? ''

  const onViewServiceType = () => {
    dispatch(setService({ Name: t.Name, ServiceTypeId: serviceTypeId }))
    void navigate(`/management/view-service?id=${serviceTypeId}`, {
      state: { search: location.search },
    })
  }

  const onRowKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Ignore keys bubbling up from children — notably the drag handle, whose Space/arrow keys
    // drive @hello-pangea/dnd's keyboard sensor (lift/move/drop) and must not navigate the row.
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onViewServiceType()
    }
  }

  const visitCount = t.VisitTypes?.length ?? 0
  const hasPayg = t.VisitTypes?.some((v) => (v.Price ?? 0) > 0) ?? false
  const visitMeta = [`${visitCount} visit type${visitCount !== 1 ? 's' : ''}`, hasPayg ? 'PAYG' : null]
    .filter((part): part is string => part !== null)
    .join(' · ')

  const rowClassName = [
    'flex items-center gap-[12px] py-[14px] px-[24px] bg-white cursor-pointer hover:bg-[#f2fafb]' /* surface-teal-soft — no exact DS token */,
    isDragging ?
      'rounded-large bg-[#f2fafb] shadow-[0_4px_12px_rgba(37,56,69,0.12)]' /* surface-teal-soft — no exact DS token */
    : isLast ? ''
    : 'border-b border-shade-10',
  ].join(' ')

  return (
    <div
      className={rowClassName}
      onClick={onViewServiceType}
      onKeyDown={onRowKeyDown}
      role='button'
      tabIndex={0}
      data-testid='pw_service-type-row'>
      <div
        className='flex shrink-0 cursor-grab items-center px-[2px] py-[4px] active:cursor-grabbing'
        role='presentation'
        data-testid='pw_service-type-drag-handle'
        {...dragHandleProps}
        onClick={(e) => e.stopPropagation()}>
        <FontAwesomeIcon icon={faGripDotsVertical} className='w-[14px] text-text-light' />
      </div>
      <div className='flex h-sp-5 w-sp-5 shrink-0 items-center justify-center rounded-full bg-deep-navy-10 text-text-dark'>
        <KitMedicalIcon iconSize='m' />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-[2px]'>
        <span className='truncate text-sm font-semibold leading-5 text-text-dark' data-testid='pw_service-type-name'>
          {t.Name}
        </span>
        <span className='truncate text-xs font-normal leading-4 text-text-light'>{visitMeta}</span>
      </div>
      <div className='shrink-0'>
        <Badge variant='active' size='s'>
          Active
        </Badge>
      </div>
      <ChevronRightIcon iconSize='s' className='shrink-0 text-text-light' />
    </div>
  )
}
